import { logActivity } from "../services/activityService";
import { byId } from "./dom";

/* "Install app" button in the top bar.
   - Android / desktop Chrome & Edge: the browser offers an install prompt; we hold it and open it on click.
   - iPhone / iPad (and any browser with no prompt): the button opens a short "how to install" box.
   - Already installed (opened from the home screen): the button is hidden. */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
let render: (() => void) | null = null;

// The browser can fire this before anyone logs in, so listen from the moment the script loads.
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  render?.();
});
window.addEventListener("appinstalled", () => {
  logActivity("install_app");
  installed = true;
  deferredPrompt = null;
  render?.();
});

function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

function showHelp(): void {
  const wrap = document.createElement("div");
  wrap.className = "topup-overlay";
  wrap.setAttribute("role", "dialog");
  wrap.setAttribute("aria-modal", "true");
  const panel = document.createElement("div");
  panel.className = "topup-panel";

  const h = document.createElement("h2");
  h.textContent = "Install PropertyVest";
  const p = document.createElement("p");
  p.textContent = isIos()
    ? "On your iPhone or iPad: tap the Share icon at the bottom of Safari, then choose “Add to Home Screen”."
    : "Open your browser menu and choose “Install app” or “Add to Home screen”. If you don’t see it, open this page in Chrome.";
  const close = document.createElement("button");
  close.type = "button";
  close.className = "btn-primary topup-btn";
  close.textContent = "Got it";
  close.addEventListener("click", () => wrap.remove());
  wrap.addEventListener("click", (e) => { if (e.target === wrap) wrap.remove(); });

  panel.append(h, p, close);
  wrap.appendChild(panel);
  document.body.appendChild(wrap);
}

export function initInstallApp(): void {
  const btn = byId<HTMLButtonElement>("install-btn");

  render = (): void => {
    btn.hidden = installed || isStandalone();
  };

  btn.addEventListener("click", async () => {
    if (!deferredPrompt) { showHelp(); return; }
    const promptEvent = deferredPrompt;
    deferredPrompt = null; // a prompt can only be used once
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice.outcome === "accepted") installed = true;
    } catch {
      /* the browser refused; fall back to the how-to box next time */
    }
    render?.();
  });

  render();
}
